'use client'

import { AddToCartModal } from '@/components/cart/add-to-cart-modal'
import { Icon } from '@/components/ui/icon'
import { useTapLock } from '@/hooks/use-tap-lock'
import { useUnitOfMeasure } from '@/hooks/useUnitOfMeasure'
import { useAppDispatch } from '@/lib/store'
import { addToCart, openCartDrawer } from '@/lib/store/features/cart/cartSlice'
import { IProductVariant } from '@/lib/types/core/product-variant'
import { ProductItem } from '@/lib/types/operations/product'
import { ISelectedVariant } from '@/lib/types/operations/product-variant-selection'
import { Button } from '@/components/ui/button'
import { showToast, ToastSuppress } from '@/lib/utils/toast'
import { useSession } from 'next-auth/react'
import { useCallback, useMemo, useState } from 'react'

interface AddToCartButtonProps {
  product?: ProductItem
  variant?: ISelectedVariant
  size?: 'sm' | 'md' | 'lg'
  className?: string
  buttonText?: string
  isDisabled?: boolean
  showPrice?: boolean
  iconOnly?: boolean
  buttonVariant?: string
}

// Add a helper function to validate variant price
function hasValidPrice(variant: ISelectedVariant | undefined): boolean {
  if (!variant) return false

  const pricePerUnit = typeof variant.pricePerUnit === 'number' ? variant.pricePerUnit : 0
  const rollPrice = typeof variant.rollPrice === 'number' ? variant.rollPrice : 0

  // If using roll price, check roll price. Otherwise check price per yard
  if (variant.isFullRoll) {
    return rollPrice > 0
  }

  return pricePerUnit > 0
}

// Checks if a simple product has valid inventory or price
const hasValidProductInventoryOrPrice = (product?: ProductItem): boolean => {
  if (!product) return false

  // Check product's inventoryItems first
  if (product.inventoryItems && Array.isArray(product.inventoryItems) && product.inventoryItems.length > 0) {
    const totalQuantity = product.inventoryItems.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0)
    if (totalQuantity > 0) return true
  }

  // Fallback to single inventory if available
  if (product.inventory?.quantity && product.inventory.quantity > 0) {
    return true
  }

  // If no inventory data but has a valid price, allow adding to cart
  if (typeof product.price === 'number' && product.price > 0) {
    return true
  }

  return false
}

// Checks if a variant has valid inventory
const hasVariantInventory = (variant?: ISelectedVariant, product?: ProductItem): boolean => {
  if (!variant || !variant.variantId || !product) return false

  // Check product's inventoryItems for this variant
  const variantInventoryItems = product.inventoryItems?.filter((item) => item.variantId === variant.variantId) || []

  const totalVariantQuantity = variantInventoryItems.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0)

  if (totalVariantQuantity > 0) return true

  // If no variant inventory found but has a valid price and the product has general inventory,
  // consider it available
  if (hasValidPrice(variant) && hasValidProductInventoryOrPrice(product)) {
    return true
  }

  return false
}

export function AddToCartButton({
  product,
  variant,
  size = 'md',
  className,
  buttonText = 'Add to Cart',
  isDisabled: externalDisabled = false,
  iconOnly,
  buttonVariant = 'solid'
}: AddToCartButtonProps) {
  // Core state
  const dispatch = useAppDispatch()
  const { data: session } = useSession()
  const priceTier = ((session?.user as any)?.priceTier as 'RETAIL' | 'WHOLESALE') || 'RETAIL'
  const [showModal, setShowModal] = useState(false)
  // Synchronous lock: a rapid double-tap must not double the cart quantity
  const acquireTapLock = useTapLock()

  // Use unit of measure hooks for product and variant at the top level
  const productUnitMeasure = useUnitOfMeasure(product?.unitOfMeasure, null, product)

  const variantUnitMeasure = useUnitOfMeasure(variant?.unitOfMeasure, variant)

  // Check if this product has variants
  const hasVariants = useMemo(() => {
    if (!product) return false
    return Array.isArray(product.variants) && product.variants.length > 0
  }, [product])

  // Helper functions
  const addSimpleProductToCart = useCallback(() => {
    if (!product) return

    // Check if we have a valid inventory or price
    if (!hasValidProductInventoryOrPrice(product)) {
      showToast({ title: 'Product is out of stock or has invalid pricing', color: 'danger', timeout: 5000 })
      return
    }

    // Use the unit of measure from the hook
    const unitOfMeasure = productUnitMeasure.unitOfMeasure

    dispatch(
      addToCart({
        productId: product.id || '',
        quantity: 1,
        product: {
          ...product,
          price: product.price || 0
        },
        unitOfMeasure
      })
    )

    showToast({
      title: `Added ${productUnitMeasure.formatQuantity(1)} to cart`,
      color: 'success',
      timeout: 3000,
      skipIf: ToastSuppress.onMobile
    })
    dispatch(openCartDrawer())
  }, [product, productUnitMeasure, dispatch])

  const addVariantToCart = useCallback(() => {
    if (!product || !variant) return

    // Validate variant price
    if (!hasValidPrice(variant)) {
      showToast({ title: 'Cannot add product with invalid price', color: 'danger', timeout: 5000 })
      return
    }

    // Validate variant inventory
    if (!hasVariantInventory(variant, product)) {
      showToast({ title: 'This variant is out of stock', color: 'danger', timeout: 5000 })
      return
    }

    // Use the unit of measure from the hook
    const unitOfMeasure = variantUnitMeasure.unitOfMeasure

    // Create product with selected variant info in the display name
    const variantDisplayName = `${product.name} - ${variant.color} ${variant.width}"×${variant.height}"`

    // Calculate price based on whether it's a full roll or not
    const variantPrice =
      variant.isFullRoll && variant.rollPrice ? variant.rollPrice : variant.pricePerUnit * variant.selectedLength

    // Clone the product with modified display name and price
    const enhancedProduct = {
      ...product,
      id: `${product.id}_${variant.variantId}`, // Ensure unique ID for cart items
      name: variantDisplayName,
      price: variantPrice
    }

    // Use hook for quantity formatting
    const quantity = variant.isFullRoll ? 1 : variant.selectedLength

    // Add the variant to cart
    dispatch(
      addToCart({
        productId: product.id || '',
        quantity: quantity,
        variantId: variant.variantId,
        product: enhancedProduct,
        purchaseMode: variant.isFullRoll ? 'full-roll' : 'custom-cut',
        pieces: 1,
        lengthPerPiece: variant.isFullRoll ? undefined : variant.selectedLength,
        unitOfMeasure: unitOfMeasure
      })
    )

    showToast({
      title: `Added ${variantUnitMeasure.formatQuantity(quantity)} to cart`,
      color: 'success',
      timeout: 3000,
      skipIf: ToastSuppress.onMobile
    })
    dispatch(openCartDrawer())
  }, [product, variant, variantUnitMeasure, dispatch])

  // Main click handler for the Add to Cart button
  const handleAddToCart = useCallback(() => {
    // If we already have a selected variant, add it directly
    if (variant) {
      // Second tap inside the lock window is a no-op, not a quantity increment
      if (!acquireTapLock()) return
      try {
        addVariantToCart()
      } catch (error) {
        console.error('Failed to add variant to cart:', error)
        showToast({ title: 'Failed to add to cart', color: 'danger', timeout: 5000 })
      }
      return
    }

    // Check if this product has variants requiring selection
    if (hasVariants) {
      // Show AddToCartModal for variant configuration
      setShowModal(true)
      return
    }

    // For simple products without variants
    if (product) {
      // Second tap inside the lock window is a no-op, not a quantity increment
      if (!acquireTapLock()) return
      try {
        addSimpleProductToCart()
      } catch (error) {
        console.error('Failed to add product to cart:', error)
        showToast({ title: 'Failed to add to cart', color: 'danger', timeout: 5000 })
      }
    }
  }, [variant, hasVariants, product, addVariantToCart, addSimpleProductToCart, acquireTapLock])

  // Calculate button disabled state
  const isButtonDisabled = useMemo(() => {
    // External disabled flag takes priority
    if (externalDisabled) return true

    // If we have a variant, check if it has a valid price and inventory
    if (variant) {
      // Check both price and inventory
      const hasPrice = hasValidPrice(variant)
      const hasInventory = hasVariantInventory(variant, product)
      return !(hasPrice && hasInventory)
    }

    // If it's a product without variants, check if it has valid inventory or price
    if (!hasVariants && product) return !hasValidProductInventoryOrPrice(product)

    // By default, enable the button if we have a product with variants
    return false
  }, [externalDisabled, variant, hasVariants, product])

  return (
    <>
      <Button
        size={size}
        className={className}
        onPress={handleAddToCart}
        isDisabled={isButtonDisabled}
        variant={buttonVariant}
        isIconOnly={iconOnly}
      >
        {iconOnly ? (
          <Icon icon='cart-plus' className='text-xl' />
        ) : (
          <>
            <Icon icon='cart-plus' className='text-xl' aria-hidden='true' />
            {isButtonDisabled ? (
              variant ? (
                !hasValidPrice(variant) ? (
                  'Invalid Price'
                ) : !hasVariantInventory(variant, product) ? (
                  'Out of Stock'
                ) : (
                  'Unavailable'
                )
              ) : !hasValidProductInventoryOrPrice(product) ? (
                'Out of Stock'
              ) : (
                'Unavailable'
              )
            ) : hasVariants && !variant ? (
              'Select Options'
            ) : (
              buttonText
            )}
          </>
        )}
      </Button>

      <AddToCartModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        product={product!}
        variant={(product?.variants?.[0] ?? null) as IProductVariant | null}
        priceTier={priceTier}
      />
    </>
  )
}
